import React, { useState, useEffect, useRef } from 'react';

interface PriceRangeSliderProps {
  min: number;
  max: number;
  value: { min: number; max: number };
  onChange: (min: number, max: number) => void;
}

const PriceRangeSlider: React.FC<PriceRangeSliderProps> = ({ 
  min, 
  max, 
  value, 
  onChange 
}) => {
  const [minValue, setMinValue] = useState(value.min);
  const [maxValue, setMaxValue] = useState(value.max);
  const minInputRef = useRef<HTMLInputElement>(null);
  const maxInputRef = useRef<HTMLInputElement>(null);
  const sliderTrackRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMinValue(value.min);
    setMaxValue(value.max);
  }, [value]);

  const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newMin = Math.min(Number(e.target.value), maxValue - 1);
    setMinValue(newMin);
    onChange(newMin, maxValue);
  };

  const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newMax = Math.max(Number(e.target.value), minValue + 1);
    setMaxValue(newMax);
    onChange(minValue, newMax);
  };

  // Calculate percentages for styling
  const minPercent = ((minValue - min) / (max - min)) * 100;
  const maxPercent = ((maxValue - min) / (max - min)) * 100;

  return (
    <div className="price-range-slider">
      <div className="price-range-header">
        <h6>Price Range</h6>
        <div className="price-display">
          <span>${minValue} - ${maxValue}</span>
        </div>
      </div>
      
      <div className="slider-container">
        <div className="slider-track" ref={sliderTrackRef}>
          <div 
            className="slider-range"
            style={{
              left: `${minPercent}%`,
              width: `${maxPercent - minPercent}%`
            }}
          />
        </div>
        
        <input
          ref={minInputRef}
          type="range"
          min={min}
          max={max}
          value={minValue}
          onChange={handleMinChange}
          className="range-input range-input-min"
        />
        
        <input
          ref={maxInputRef}
          type="range"
          min={min}
          max={max}
          value={maxValue}
          onChange={handleMaxChange}
          className="range-input range-input-max"
        />
      </div>
      
      <div className="price-range-labels">
        <span>${min}</span>
        <span>${max}</span>
      </div>
      
      <div className="price-inputs">
        <div className="price-input-group">
          <label>Min</label>
          <input
            type="number"
            value={minValue}
            min={min}
            max={maxValue - 1}
            onChange={(e) => {
              const newMin = Math.max(min, Math.min(Number(e.target.value), maxValue - 1));
              setMinValue(newMin);
              onChange(newMin, maxValue);
            }}
          />
        </div>
        <div className="price-input-group">
          <label>Max</label>
          <input
            type="number"
            value={maxValue}
            min={minValue + 1}
            max={max}
            onChange={(e) => {
              const newMax = Math.min(max, Math.max(Number(e.target.value), minValue + 1));
              setMaxValue(newMax);
              onChange(minValue, newMax);
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default PriceRangeSlider; 